import { kitDateParts, kitWords, type KitTextLocale } from '../kitText';
import { hasLinkNote, type StoryFormatId } from './storyFormats';
import type { KitStory, KitStoryMoment } from './storyTypes';

/**
 * The copy of a Story (owner answers 2026-10-08, design `asset-redesign/b Story - new`) and the one
 * builder of the Story texts. The server (`buildKitStory`) and the web preview use it, so the
 * preview shows the words of the file. The words come from the kit text table (`../kitText.ts`) in
 * the language of the kit (owner request 2026-10-29); `STORY_COPY` is the English set, kept for the
 * callers and the tests that read the English words.
 */
export const STORY_COPY = {
  newSingle: 'New single',
  outNow: 'Out now',
  preSave: 'Pre-save now',
  listen: 'Listen now',
  daysToGo: 'days to go',
  dayToGo: 'day to go',
  out: (dateLabel: string): string => `Out ${dateLabel}`,
  tomorrow: (dateLabel: string): string => `Tomorrow · ${dateLabel}`,
  /** The pill of a Story and a feed post before the release when the song has no pre-save page (owner answers 2026-10-08). */
  comingSoon: 'Coming soon',
  /** The words before the address under the pill of a feed post (owner answer 2026-10-08). */
  linkInBio: 'Link in bio',
} as const;

/** The countdown days of a Story: 7, 3 and 1 days before the release. */
export type StoryCountdownDays = 7 | 3 | 1;

/** "12 Oct" for `2026-10-12` ("12. Okt." in German); null for a date that the code cannot read. Pure. */
export function releaseDateLabel(releaseDate: string | null, locale?: KitTextLocale | null): string | null {
  const parts = kitDateParts(releaseDate);
  return parts === null ? null : kitWords(locale).dateLabel(parts.day, parts.month);
}

/** "Fri 16 Oct" for `2026-10-16` (the date ring of an announce feed post); null for a date that the code cannot read. Pure. */
export function releaseDayLabel(releaseDate: string | null, locale?: KitTextLocale | null): string | null {
  const parts = kitDateParts(releaseDate);
  return parts === null ? null : kitWords(locale).dayLabel(parts.weekday, parts.day, parts.month);
}

/** True from the release day on, and for a song with no release date. Pure. */
export function isReleasedOn(releaseDate: string | null, today: string): boolean {
  return releaseDate === null || releaseDate.slice(0, 10) <= today.slice(0, 10);
}

/**
 * The text of the CTA pill (owner answer 1): "Listen now" on an out-now Story; "Pre-save now" on a
 * pre-save Story, a countdown and an announce feed post; on a lyric Story "Pre-save now" before the
 * release day and "Listen now" from the release day on. A Story or a feed post of a song with no
 * published pre-save page (`presave: false`) says "Coming soon" where it would say "Pre-save now"
 * (owner answers 2026-10-08). `presave` absent: not known, "Pre-save now". Pure.
 */
export function storyPillText(
  moment: KitStoryMoment,
  releaseDate: string | null,
  today: string,
  presave?: boolean,
  locale?: KitTextLocale | null
): string {
  const words = kitWords(locale);
  const preSave = presave === false ? words.comingSoon : words.preSaveNow;
  switch (moment) {
    case 'outNow':
      return words.listenNow;
    case 'lyric':
      return isReleasedOn(releaseDate, today) ? words.listenNow : preSave;
    case 'preSave':
    case 'countdown':
    case 'announce':
      return preSave;
    default: {
      const exhaustive: never = moment;
      return exhaustive;
    }
  }
}

/** The line under the day number: "day to go" for one day, else "days to go". */
export function storyDayLabel(days: StoryCountdownDays, locale?: KitTextLocale | null): string {
  const words = kitWords(locale);
  return days === 1 ? words.dayToGo : words.daysToGo;
}

/** The date ring of a countdown: "Tomorrow · 16 Oct" one day before, else "Out 16 Oct". Null with no date. */
export function storyWhenText(
  days: StoryCountdownDays,
  dateLabel: string | null,
  locale?: KitTextLocale | null
): string | null {
  if (dateLabel === null) {
    return null;
  }
  const words = kitWords(locale);
  return days === 1 ? words.tomorrowDate(dateLabel) : words.outDate(dateLabel);
}

/**
 * The address under the pill (owner answer 2): the published SmartLink address of the song with no
 * scheme, no trailing slash, no query and no fragment (`laziibee.music24.link/swamps`, or the custom
 * domain of the artist). Null when the song has no published SmartLink. Pure.
 */
export function storyLinkText(url: string | null | undefined): string | null {
  if (url === null || url === undefined || url.trim() === '') {
    return null;
  }
  const bare = url
    .trim()
    .replace(/^[a-z][a-z0-9+.-]*:\/\//i, '')
    .replace(/[?#].*$/, '')
    .replace(/\/+$/, '');
  return bare === '' ? null : bare;
}

/**
 * The word of the lyric that lights in the accent: a word of the title when the lyric holds one
 * (the design lights "Forests"), else the last word. Pure.
 */
export function storyNowWord(lyric: string, title: string): string | null {
  const clean = (word: string): string => word.replace(/[^\p{L}\p{N}'’-]/gu, '');
  const words = lyric
    .split(/\s+/)
    .map(clean)
    .filter((word) => word !== '');
  const titleWords = new Set(title.split(/\s+/).map((word) => clean(word).toLowerCase()));
  const match = [...words].reverse().find((word) => titleWords.has(word.toLowerCase()));
  return match ?? words[words.length - 1] ?? null;
}

export interface BuildStoryInput {
  /** The frame: a Story (absent) or a feed post (`feed45`, `feed11`). */
  format?: StoryFormatId;
  moment: KitStoryMoment;
  title: string;
  artist: string;
  /** `YYYY-MM-DD`, or null. */
  releaseDate: string | null;
  /** Today as `YYYY-MM-DD` (UTC). */
  today: string;
  /** The day of a countdown Story; absent: 7. */
  countdownDays?: StoryCountdownDays | null;
  /** The lyric of a lyric Story (one line, or two lines with a line break). Absent: the title. */
  lyric?: string | null;
  /** The address under the pill, already with no scheme (`storyLinkText`); null: none. */
  link: string | null;
  /**
   * True when the song has a published pre-save page. False: the pill of a moment before the
   * release says "Coming soon" (a Story and a feed post). Absent: not known, "Pre-save now".
   */
  presave?: boolean;
  withLogo: boolean;
  /** The language of the words (`kitText.ts`); absent: English. The title, the artist and the lyric stay as they are. */
  locale?: KitTextLocale | null;
}

/**
 * The texts of one Story or feed post, from the song, the moment, the release date and today. A
 * feed post adds "Link in bio" before the address (owner answer 2026-10-08). Pure.
 */
export function buildStory(input: BuildStoryInput): KitStory {
  const { locale } = input;
  const words = kitWords(locale);
  const dateLabel = releaseDateLabel(input.releaseDate, locale);
  const feed = hasLinkNote(input.format);
  const base: KitStory = {
    ...(input.format === undefined ? {} : { format: input.format }),
    moment: input.moment,
    title: input.title,
    artist: input.artist,
    kicker: null,
    hero: null,
    nowWord: null,
    dayLabel: null,
    when: null,
    cta: storyPillText(input.moment, input.releaseDate, input.today, input.presave, locale),
    link: input.link,
    ...(feed ? { linkNote: input.link === null ? null : words.linkInBio } : {}),
    withLogo: input.withLogo,
  };
  switch (input.moment) {
    case 'preSave':
      return { ...base, kicker: words.newSingle, hero: dateLabel };
    case 'outNow':
      return { ...base, hero: words.outNow };
    case 'lyric': {
      const lyric =
        input.lyric !== undefined && input.lyric !== null && input.lyric.trim() !== '' ? input.lyric : input.title;
      return { ...base, hero: lyric, nowWord: storyNowWord(lyric, input.title) };
    }
    case 'countdown': {
      const days = input.countdownDays ?? 7;
      return {
        ...base,
        hero: String(days),
        dayLabel: storyDayLabel(days, locale),
        when: storyWhenText(days, dateLabel, locale),
      };
    }
    case 'announce': {
      const day = releaseDayLabel(input.releaseDate, locale);
      return { ...base, hero: words.newSingle, when: day === null ? null : words.outDate(day) };
    }
    default: {
      const exhaustive: never = input.moment;
      return exhaustive;
    }
  }
}
